// Turns a logical model into diagram content: one card per entity with rows for properties and
// sections for owned and complex types, plus relationship and inheritance edges. Pure and testable.
import { isPureJoin, typeLabel, type ChangeState, type ComplexSpec, type DeleteEffect, type EntitySpec, type ModelSpec, type OwnedSpec, type PropertySpec, type RelationshipSpec } from '@modelviewer/core';

export { isPureJoin, typeLabel };

export type PropertyMode = 'all' | 'keys' | 'none';

/** Text width in pixels for the fonts used on a card. */
export type Measure = (text: string, font: 'title' | 'base' | 'name' | 'type' | 'section' | 'tag') => number;

/** Rough widths for tests and for use before fonts load. */
export const approximateMeasure: Measure = (text, font) =>
  text.length * { title: 8, base: 6.8, name: 7.4, type: 7.1, section: 7.3, tag: 6.4 }[font];

/** How a card, row or line changed since the previous model (timeline and compare views). */
export type DiffState = ChangeState;

/** The tag a changed card shows in its header. */
export const DIFF_LABEL: Record<DiffState, string> = { added: 'new', removed: 'removed', changed: 'changed' };

/** The tags above a card while cascade paths are on: what deleting the selected row does to its rows. */
export const EFFECT_LABEL: Record<DeleteEffect, string> = { delete: 'deleted', setNull: 'set null', block: 'blocks' };

export interface GraphOptions {
  properties: PropertyMode;
  /** Draw pure join entities (only two foreign keys) as many-to-many links. */
  collapseJoins: boolean;
  measure?: Measure;
}

export interface RowData {
  /** Unique within the card: `Url`, `Address.City`, `CreditLimit.Amount`. */
  id: string;
  name: string;
  type: string;
  optional: boolean;
  pk: boolean;
  fk: boolean;
  ak: boolean;
  generated: boolean;
  concurrency: boolean;
  depth: number;
  diff?: DiffState;
  /** What changed, for the tooltip: `was Name`, `string(100) → string(200)`. */
  note?: string;
  /** Code map rows: what kind of member the row is (instead of key icons). */
  member?: MemberIcon;
  /** Code map rows: how many tests reach the member, shown after its name. */
  tests?: number;
}

/** The icon of a code map member row. */
export type MemberIcon = 'method' | 'constructor' | 'property' | 'field' | 'event' | 'const' | 'enumMember' | 'operator' | 'data' | 'fake';

export interface SectionData {
  id: string;
  /** `type`: a class, record, interface, struct, enum or delegate on a code map card. */
  kind: 'owned' | 'complex' | 'type';
  name: string;
  type: string;
  collection: boolean;
  json: boolean;
  optional: boolean;
  depth: number;
  diff?: DiffState;
}

export type Line = { kind: 'row'; row: RowData } | { kind: 'section'; section: SectionData };

export interface EntityNodeData {
  [key: string]: unknown;
  /** Diagram node id: the entity key, or a name that stays the same across a timeline. */
  id: string;
  /** Entity key in the model this card was built from. */
  key: string;
  label: string;
  namespace: string | null;
  /** Database schema of its table or view (the model's default schema when it has none of its own). */
  schema: string | null;
  badges: string[];
  base: string | null;
  table: string | null;
  lines: Line[];
  /** Row ids that edges attach to; only these get connection handles. */
  anchors: string[];
  width: number;
  height: number;
  diff?: DiffState;
  /** Shown after the title, for example `was Author`. */
  note?: string;
  /** Key of this entity in the older model, when it was renamed. */
  previousKey?: string;
  /** Drawn faintly (the code map's test files, when dimmed). */
  muted?: boolean;
  /** Cascade paths: what deleting the selected row does to this entity's rows (tags above the card, outside its size). */
  effects?: DeleteEffect[];
  /** Cascade paths: the entity whose row is deleted. */
  deleteStart?: boolean;
}

export interface GraphEdge {
  id: string;
  /** `call` (a member calling or creating another) and `uses` (a file using a type of another file): code map lines. */
  kind: 'relationship' | 'inheritance' | 'manyToMany' | 'call' | 'uses';
  /** Dependent (relationship), derived type (inheritance), first side (many-to-many) or caller (call, uses). */
  source: string;
  target: string;
  sourceRow: string | null;
  targetRow: string | null;
  required: boolean;
  unique: boolean;
  requiredDependent: boolean;
  label: string | null;
  /** Index into model.relationships (relationship edges). */
  relationship: number | null;
  diff?: DiffState;
  /** Code map: the line is a guess by name (drawn dashed). */
  guess?: boolean;
  /** Code map: how many calls or uses the line stands for. */
  count?: number;
  /** Drawn faintly (a line to or from a dimmed card). */
  muted?: boolean;
  /** Cascade paths: what deleting the principal's row does to the dependent's rows along this line. */
  effect?: DeleteEffect;
  /** Cascade paths, SQL Server: one of the cascading paths it refuses, shown as this label. */
  conflict?: string;
}

export interface Graph {
  nodes: EntityNodeData[];
  edges: GraphEdge[];
}

export const ROW_HEIGHT = 22;
export const HEADER_HEIGHT = 36;
const BODY_PADDING = 6;
const MIN_WIDTH = 190;
const MAX_WIDTH = 460;


/** Resolves an entity key or owned path to its card and the row prefix inside it. */
export function locate(model: ModelSpec, name: string): { node: string; prefix: string; spec: EntitySpec | OwnedSpec } | null {
  if (model.entities[name]) return { node: name, prefix: '', spec: model.entities[name]! };
  const parts = name.split('.');
  for (let i = parts.length - 1; i >= 1; i--) {
    const key = parts.slice(0, i).join('.');
    const entity = model.entities[key];
    if (!entity) continue;
    let spec: EntitySpec | OwnedSpec | undefined = entity;
    for (const nav of parts.slice(i)) spec = spec?.owned?.[nav];
    if (spec) return { node: key, prefix: `${parts.slice(i).join('.')}.`, spec };
  }
  return null;
}

/** The schema an entity's rows live in; derived entities without their own table use their base's. */
function schemaOf(model: ModelSpec, key: string): string | null {
  const seen = new Set<string>();
  for (let k: string | undefined = key; k && !seen.has(k); k = model.entities[k]?.base) {
    seen.add(k);
    const m = model.entities[k]?.mapping;
    const schema = m?.view ? m.viewSchema : m?.schema;
    if (schema) return schema;
    if (m?.table || m?.view) break;
  }
  return model.defaultSchema ?? null;
}

function keyOf(model: ModelSpec, key: string): string[] {
  let e: EntitySpec | undefined = model.entities[key];
  const seen = new Set<string>();
  let current = key;
  while (e && !seen.has(current)) {
    seen.add(current);
    if (e.key) return e.key;
    if (!e.base) break;
    current = e.base;
    e = model.entities[current];
  }
  return [];
}


// Mirrors the card CSS: paddings, 14px icon column, 6px gaps, 11px flag icons, the test count
// (10px icon, 2px gap, its number), 12px before the type.
function cardWidth(measure: Measure, node: Pick<EntityNodeData, 'label' | 'base' | 'badges' | 'lines' | 'note' | 'diff'>): number {
  const { label, base, badges, lines, note, diff } = node;
  let width = 12 + measure(label, 'title') + 10 + 2;
  if (base) width += 6 + measure(`: ${base}`, 'base');
  if (note) width += 6 + measure(note, 'base');
  for (const b of [...badges, ...(diff ? [DIFF_LABEL[diff]] : [])]) width += 4 + measure(b.toUpperCase(), 'tag') + 12;
  for (const line of lines) {
    let w: number;
    if (line.kind === 'row') {
      const r = line.row;
      const flags = (r.generated ? 1 : 0) + (r.concurrency ? 1 : 0);
      const tests = r.tests ? 6 + 10 + 2 + measure(String(r.tests), 'tag') : 0;
      w = 8 + r.depth * 12 + 14 + 6 + measure(r.name, 'name') + flags * 17 + tests + 6 + 12 + measure(r.type, 'type') + 10 + 2;
    } else {
      const s = line.section;
      const type = `${s.type}${s.collection ? '[]' : ''}${s.optional ? '?' : ''}`;
      w = 10 + s.depth * 12 + 12 + 6 + measure(s.name, 'section') + 12 + measure(type, 'type') + (s.json ? 6 + measure('JSON', 'tag') + 12 : 0) + 10 + 2;
    }
    width = Math.max(width, w);
  }
  return Math.ceil(Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, width + 4)));
}

const cardHeight = (lines: Line[]) => HEADER_HEIGHT + (lines.length ? lines.length * ROW_HEIGHT + BODY_PADDING * 2 : 0);

/** Recomputes a card's size after its lines, badges or note changed. */
export function sizeNode(node: EntityNodeData, measure: Measure = approximateMeasure): EntityNodeData {
  return { ...node, width: cardWidth(measure, node), height: cardHeight(node.lines) };
}

export function buildGraph(model: ModelSpec, options: GraphOptions): Graph {
  const joins = new Map<string, [RelationshipSpec, RelationshipSpec]>();
  if (options.collapseJoins) {
    for (const key of Object.keys(model.entities)) {
      const pair = isPureJoin(model, key);
      if (pair) joins.set(key, pair);
    }
  }

  // Foreign key rows per card, by row id.
  const fkRows = new Map<string, Set<string>>();
  model.relationships.forEach((r) => {
    const loc = locate(model, r.dependent);
    if (!loc) return;
    const set = fkRows.get(loc.node) ?? new Set<string>();
    for (const p of r.foreignKey) set.add(`${loc.prefix}${p}`);
    fkRows.set(loc.node, set);
  });

  const nodes: EntityNodeData[] = [];
  for (const [key, e] of Object.entries(model.entities)) {
    if (joins.has(key)) continue;
    const fks = fkRows.get(key) ?? new Set<string>();
    // Derived entities show only their own properties; the inherited key lives on the base card.
    const pk = new Set(e.key ?? []);
    const ak = new Set((e.alternateKeys ?? []).flat());
    const lines: Line[] = [];
    const include = (isKey: boolean) => options.properties === 'all' || (options.properties === 'keys' && isKey);
    const addRows = (props: Record<string, PropertySpec>, prefix: string, depth: number, hidden: Set<string>, keys: Set<string>) => {
      for (const [name, p] of Object.entries(props)) {
        if (hidden.has(name)) continue;
        const id = `${prefix}${name}`;
        const row: RowData = {
          id,
          name,
          type: typeLabel(p),
          optional: !p.required,
          pk: keys.has(name),
          fk: fks.has(id),
          ak: prefix === '' && ak.has(name),
          generated: p.generated !== undefined,
          concurrency: p.concurrencyToken === true,
          depth,
        };
        if (include(row.pk || row.fk || row.ak)) lines.push({ kind: 'row', row });
      }
    };
    const addComplex = (complex: Record<string, ComplexSpec> | undefined, prefix: string, depth: number) => {
      for (const [name, c] of Object.entries(complex ?? {})) {
        if (options.properties === 'none') continue;
        const id = `${prefix}${name}`;
        if (options.properties === 'all') {
          lines.push({
            kind: 'section',
            section: { id, kind: 'complex', name, type: c.type, collection: !!c.collection, json: !!c.mapping?.json, optional: !c.required, depth },
          });
        }
        addRows(c.properties, `${id}.`, depth + 1, new Set(), new Set());
        addComplex(c.complex, `${id}.`, depth + 1);
      }
    };
    const addOwned = (owned: Record<string, OwnedSpec> | undefined, prefix: string, depth: number) => {
      for (const [nav, o] of Object.entries(owned ?? {})) {
        if (options.properties === 'none') continue;
        const id = `${prefix}${nav}`;
        if (options.properties === 'all') {
          lines.push({
            kind: 'section',
            section: { id, kind: 'owned', name: nav, type: o.type, collection: !!o.collection, json: !!o.mapping?.json, optional: !o.required, depth },
          });
        }
        // The owner key only links the owned rows to their owner; hide it unless it is also a real key.
        const hidden = new Set(o.key ? o.ownerKey.filter((k) => !o.key!.includes(k)) : o.ownerKey);
        addRows(o.properties, `${id}.`, depth + 1, hidden, new Set(o.key ?? []));
        addComplex(o.complex, `${id}.`, depth + 1);
        addOwned(o.owned, `${id}.`, depth + 1);
      }
    };
    addRows(e.properties, '', 0, new Set(), pk);
    addComplex(e.complex, '', 0);
    addOwned(e.owned, '', 0);

    const badges: string[] = [];
    if (e.keyless) badges.push(e.mapping?.view ? 'view' : 'keyless');
    if (e.mapping?.strategy) badges.push(e.mapping.strategy.toUpperCase());
    const clr = e.clrName ?? key;
    const dot = clr.lastIndexOf('.');
    const label = key.includes('.') ? key.slice(key.lastIndexOf('.') + 1) : key;
    const width = cardWidth(options.measure ?? approximateMeasure, { label, base: e.base ?? null, badges, lines });
    nodes.push({
      id: key,
      key,
      label,
      namespace: dot > 0 ? clr.slice(0, dot) : null,
      schema: schemaOf(model, key),
      badges,
      base: e.base ?? null,
      table: e.mapping?.table ?? null,
      lines,
      anchors: [],
      width,
      height: cardHeight(lines),
    });
  }

  const nodeByKey = new Map(nodes.map((n) => [n.key, n]));
  const rowExists = (node: EntityNodeData, row: string) => node.lines.some((l) => l.kind === 'row' && l.row.id === row);
  const edges: GraphEdge[] = [];

  model.relationships.forEach((r, index) => {
    const dep = locate(model, r.dependent);
    const prin = locate(model, r.principal);
    if (!dep || !prin || joins.has(dep.node)) return;
    const depNode = nodeByKey.get(dep.node);
    const prinNode = nodeByKey.get(prin.node);
    if (!depNode || !prinNode) return;
    const principalKey = r.principalKey ?? (prin.prefix ? ((prin.spec as OwnedSpec).key ?? (prin.spec as OwnedSpec).ownerKey) : keyOf(model, prin.node));
    const sourceRow = r.foreignKey[0] !== undefined ? `${dep.prefix}${r.foreignKey[0]}` : null;
    const targetRow = principalKey[0] !== undefined ? `${prin.prefix}${principalKey[0]}` : null;
    edges.push({
      id: `rel:${index}`,
      kind: 'relationship',
      source: dep.node,
      target: prin.node,
      sourceRow: sourceRow && rowExists(depNode, sourceRow) ? sourceRow : null,
      targetRow: targetRow && rowExists(prinNode, targetRow) ? targetRow : null,
      required: r.required,
      unique: r.cardinality === 'one-to-one',
      requiredDependent: !!r.requiredDependent,
      label: [r.dependentNavigation, r.principalNavigation].filter(Boolean).join(' / ') || null,
      relationship: index,
    });
  });

  for (const [key, pair] of joins) {
    const a = locate(model, pair[0].principal);
    const b = locate(model, pair[1].principal);
    if (!a || !b || !nodeByKey.has(a.node) || !nodeByKey.has(b.node)) continue;
    edges.push({
      id: `m2m:${key}`,
      kind: 'manyToMany',
      source: a.node,
      target: b.node,
      sourceRow: null,
      targetRow: null,
      required: false,
      unique: false,
      requiredDependent: false,
      label: key,
      relationship: null,
    });
  }

  for (const [key, e] of Object.entries(model.entities)) {
    if (e.base && nodeByKey.has(key) && nodeByKey.has(e.base)) {
      edges.push({
        id: `inh:${key}`,
        kind: 'inheritance',
        source: key,
        target: e.base,
        sourceRow: null,
        targetRow: null,
        required: true,
        unique: false,
        requiredDependent: false,
        label: null,
        relationship: null,
      });
    }
  }

  for (const edge of edges) {
    if (edge.sourceRow) nodeByKey.get(edge.source)!.anchors.push(edge.sourceRow);
    if (edge.targetRow) nodeByKey.get(edge.target)!.anchors.push(edge.targetRow);
  }
  for (const n of nodes) n.anchors = [...new Set(n.anchors)];
  return { nodes, edges };
}

/** Entities within `depth` relationship hops of `key` (for focus mode). */
export function neighborhood(graph: Graph, key: string, depth: number): Set<string> {
  const seen = new Set([key]);
  let frontier = [key];
  for (let d = 0; d < depth; d++) {
    const next: string[] = [];
    for (const k of frontier) {
      for (const e of graph.edges) {
        const other = e.source === k ? e.target : e.target === k ? e.source : null;
        if (other && !seen.has(other)) {
          seen.add(other);
          next.push(other);
        }
      }
    }
    frontier = next;
  }
  return seen;
}
